import { WrappedData, SeasonDetail } from '@/api/wrapped';

// LAMPORTS_PER_SOL constant
const LAMPORTS_PER_SOL = 1_000_000_000;

/**
 * Format lamports amount to SOL display string
 */
export function formatSolAmount(lamports: string): string {
  const num = Number(lamports);
  if (isNaN(num) || num === 0) return '0';
  const sol = num / LAMPORTS_PER_SOL;
  if (sol >= 1000) return (sol / 1000).toFixed(1) + 'K';
  if (sol >= 1) return sol.toFixed(2);
  return sol.toFixed(4);
}

/**
 * Format large numbers with K/M suffix
 */
export function formatNumber(num: number): string {
  if (num >= 1_000_000) return (num / 1_000_000).toFixed(1) + 'M';
  if (num >= 1_000) return (num / 1_000).toFixed(1) + 'K';
  return num.toString();
}

/**
 * Format points string with K/M suffix
 */
export function formatPoints(points: string): string {
  const num = Number(points);
  if (isNaN(num)) return '0';
  return formatNumber(num);
}

/**
 * Format date to "Month Day, Year" format
 */
export function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * Format a single date for display
 */
export function formatSingleDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/**
 * Format date range for streak display
 * If start and end are the same day, just show the single date
 */
export function formatDateRange(start: string, end: string): string {
  const startDate = new Date(start);
  const endDate = new Date(end);
  const startStr = startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  const endStr = endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

  // If same day, just show the single date
  if (startStr === endStr) {
    return startStr;
  }

  return `${startStr} - ${endStr}`;
}

/**
 * Get founding badge based on join date
 * Badge tiers based on join date (launch: Oct 28, 2025):
 * - Oct 28-31: "Day One Legend" (You were there at launch)
 * - Nov 1-7: "Week One Pioneer" (Early believers)
 * - Nov 8-30: "First Month Founder" (OG status)
 * - Dec 1-15: "Early Adopter" (Still ahead of most)
 * - Dec 16-31: "December Joiner" (You made it!)
 */
export function getFoundingBadge(joinDate: string): { title: string; subtitle: string } {
  // Parse the date properly to handle any format (ISO, MySQL datetime, etc.)
  const date = new Date(joinDate);

  // Handle invalid dates
  if (isNaN(date.getTime())) {
    console.warn('[getFoundingBadge] Invalid date:', joinDate);
    return { title: 'Junk.Fun Member', subtitle: 'Part of the journey' };
  }

  // Format as YYYY-MM-DD using UTC to avoid timezone issues
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  const dateStr = `${year}-${month}-${day}`;

  // Before Oct 28 or Oct 28-31, 2025: Day One Legend (earliest users)
  if (dateStr <= '2025-10-31') {
    return { title: 'Day One Legend', subtitle: 'You were there at launch' };
  }
  // Nov 1-7, 2025: Week One Pioneer
  if (dateStr >= '2025-11-01' && dateStr <= '2025-11-07') {
    return { title: 'Week One Pioneer', subtitle: 'Early believers' };
  }
  // Nov 8-30, 2025: First Month Founder
  if (dateStr >= '2025-11-08' && dateStr <= '2025-11-30') {
    return { title: 'First Month Founder', subtitle: 'OG status' };
  }
  // Dec 1-15, 2025: Early Adopter
  if (dateStr >= '2025-12-01' && dateStr <= '2025-12-15') {
    return { title: 'Early Adopter', subtitle: 'Still ahead of most' };
  }
  // Dec 16-31, 2025: December Joiner
  if (dateStr >= '2025-12-16' && dateStr <= '2025-12-31') {
    return { title: 'December Joiner', subtitle: 'You made it!' };
  }
  // Users who joined in 2026+
  return { title: 'Junk.Fun Member', subtitle: 'Part of the journey' };
}

/**
 * Personality type data mapping
 */
const PERSONALITY_DATA: Record<string, { title: string; description: string }> = {
  day_one_degen: {
    title: 'The Day One Degen',
    description: 'Joined launch week. Junked immediately. High activity, no hesitation.',
  },
  strategic_starter: {
    title: 'The Strategic Starter',
    description: 'Methodical approach. Researches before junking. Quality > quantity.',
  },
  seasonal_sprinter: {
    title: 'The Seasonal Sprinter',
    description: 'Spikes during seasons. Quiet between events. Chases rewards hard.',
  },
  chest_chaser: {
    title: 'The Chest Chaser',
    description: 'Opens chests religiously. Longest opening streaks. Loves the gamification.',
  },
  lucky_draw_believer: {
    title: 'The Lucky Draw Believer',
    description: 'Commits most points to draws. Faith in RNG. High risk, high reward.',
  },
  steady_climber: {
    title: 'The Steady Climber',
    description: 'Consistent daily activity. Longest overall streaks. Slow and steady.',
  },
  seasonal_whale: {
    title: 'The Seasonal Whale',
    description: 'Goes hard one season. Dominates leaderboards. Quality over consistency.',
  },
  completionist: {
    title: 'The Completionist',
    description: 'Participates in EVERYTHING. All seasons, chests, draws. FOMO immune.',
  },
};

/**
 * Get personality display title
 */
export function getPersonalityTitle(type: string): string {
  // Normalize key to lower case just in case
  const normalizedType = type?.toLowerCase() || '';
  // Default to Strategic Starter if type not found
  return PERSONALITY_DATA[normalizedType]?.title || PERSONALITY_DATA['strategic_starter'].title;
}

/**
 * Get personality subtitle/description
 */
export function getPersonalitySubtitle(type: string): string {
  const normalizedType = type?.toLowerCase() || '';
  // Default to Strategic Starter description if type not found
  return PERSONALITY_DATA[normalizedType]?.description || PERSONALITY_DATA['strategic_starter'].description;
}

/**
 * Get adaptive streak messaging based on longest streak
 * Thresholds:
 * - 1 day: "Hey, you showed up once. That counts."
 * - 2-3 days: "A good start"
 * - 4-6 days: "Now we're talking"
 * - 7-13 days: "Impressive dedication"
 * - 14+ days: "ABSOLUTE LEGEND"
 * Note: No emojis - Rive fonts don't support them
 */
export function getStreakMessage(streak: number): string {
  if (streak >= 14) return 'ABSOLUTE LEGEND';
  if (streak >= 7) return 'Impressive dedication';
  if (streak >= 4) return "Now we're talking";
  if (streak >= 2) return 'A good start';
  return 'Hey, you showed up once. That counts.';
}

/**
 * Get rank message based on percentile
 */
export function getRankMessage(percentile: number): string {
  if (percentile >= 99) return 'You ARE the 1%!';
  if (percentile >= 95) return 'Elite status achieved!';
  if (percentile >= 90) return 'Impessive!';
  if (percentile >= 75) return 'Upper echelon!';
  if (percentile >= 50) return 'Above average junker!';
  if (percentile >= 25) return 'Solid contributor!';
  return 'Part of the community!';
}

/**
 * Get season status label without emoji
 */
export function getSeasonStatusLabel(status: string): string {
  switch (status) {
    case 'top_performer':
      return 'Top Performer';
    case 'rising_star':
      return 'Rising Star';
    case 'participant':
      return 'Participant';
    case 'skipped':
      return 'Skipped';
    case 'in_progress':
      return 'In Progress';
    default:
      return status;
  }
}

/**
 * Status colors for Rive animation
 * - Participant: #50E4F9 (cyan)
 * - Skipped: red (#FF4444)
 * - In Progress: yellow (#FFD700)
 * - Top Performer / Rising Star: default (no override needed)
 */
export interface StatusColor {
  r: number;
  g: number;
  b: number;
  a: number;
}

/**
 * Get color for season status
 * Returns RGB values normalized to 0-1 range for Rive
 */
export function getSeasonStatusColor(status: string): StatusColor | null {
  switch (status) {
    case 'participant':
      // #50E4F9 - Cyan
      return { r: 0x50 / 255, g: 0xE4 / 255, b: 0xF9 / 255, a: 1 };
    case 'skipped':
      // Red
      return { r: 0xFF / 255, g: 0x44 / 255, b: 0x44 / 255, a: 1 };
    case 'in_progress':
      // Yellow
      return { r: 0xFF / 255, g: 0xD7 / 255, b: 0x00 / 255, a: 1 };
    default:
      // No color override for top_performer, rising_star, etc.
      return null;
  }
}

/**
 * Build season status colors for Rive (Color S1 - Color S5)
 */
export function buildSeasonStatusColors(seasonDetails: SeasonDetail[]): Record<string, StatusColor | null> {
  const sorted = [...seasonDetails].sort((a, b) => a.seasonNo - b.seasonNo);
  const result: Record<string, StatusColor | null> = {};

  for (let i = 1; i <= 5; i++) {
    const season = sorted.find((s) => s.seasonNo === i);
    const color = season ? getSeasonStatusColor(season.status) : null;
    result[`Color S${i}`] = color;
  }

  return result;
}

/**
 * Get adaptive messaging based on activity level
 * Thresholds:
 * - 0-5 items: "Every journey starts somewhere"
 * - 6-20 items: "Steady and purposeful"
 * - 21-50 items: "Serious cleanup energy"
 * - 51-100 items: "You came to WORK"
 * - 100+ items: "Absolute legend status"
 * Note: Emojis are handled via image asset swapping in RiveWrapped.tsx
 */
export function getAdaptiveMessage(data: WrappedData): string {
  const totalItems = data.totalItemsJunked;

  if (totalItems > 100) return 'Absolute legend status';
  if (totalItems >= 51) return 'You came to WORK';
  if (totalItems >= 21) return 'Serious cleanup energy';
  if (totalItems >= 6) return 'Steady and purposeful';
  return 'Every journey starts somewhere';
}

/**
 * Calculate biggest win from lucky draw wins
 */
export function getBiggestWin(data: WrappedData): string {
  if (!data.luckyDrawWins || data.luckyDrawWins.length === 0) {
    return '0';
  }
  const maxWin = Math.max(...data.luckyDrawWins.map((w) => Number(w.amountLamports)));
  return formatSolAmount(maxWin.toString());
}

/**
 * Calculate total prize from lucky draw wins
 */
export function getTotalPrize(data: WrappedData): string {
  if (!data.luckyDrawWins || data.luckyDrawWins.length === 0) {
    return '0';
  }
  const total = data.luckyDrawWins.reduce((sum, w) => sum + Number(w.amountLamports), 0);
  return formatSolAmount(total.toString());
}

/**
 * Get season rank display (first 5 seasons)
 */
export function getSeasonRanks(
  seasonDetails: SeasonDetail[]
): { rank: string; status: string }[] {
  const sorted = [...seasonDetails].sort((a, b) => a.seasonNo - b.seasonNo);
  const result: { rank: string; status: string }[] = [];

  // Get first 5 seasons (S1-S5)
  for (let i = 1; i <= 5; i++) {
    const season = sorted.find((s) => s.seasonNo === i);
    if (season) {
      result.push({
        rank: season.rank ? `#${season.rank}` : '-',
        status: getSeasonStatusLabel(season.status),
      });
    } else {
      result.push({ rank: '-', status: 'N/A' });
    }
  }

  return result;
}

/**
 * Get RNG faith message based on lottery participation
 * Shortened messages to prevent overflow
 */
export function getRngFaithMessage(data: WrappedData): string {
  const points = Number(data.pointsContributedToLottery);
  const wins = data.luckyDrawWins?.length || 0;

  if (wins >= 3) return 'RNG loves you!';
  if (wins >= 1) return 'Fortune favors bold';
  if (points >= 10000) return 'True believer!';
  if (points >= 5000) return 'High roller energy';
  if (points >= 1000) return 'Faith: Strong';
  if (points > 0) return 'Testing waters';
  return 'Not a gambler';
}

/**
 * Build text values for ViewModelInstance API (Txt 1 - 41 format)
 *
 * This function maps user stats to numbered text fields in the Rive animation.
 * Text fields are named "Txt 1" through "Txt 20", then just numbers "21" through "41".
 *
 * We include both naming conventions ("Txt X" and "X") for each field to handle
 * any naming inconsistencies in the Rive file.
 */
export function buildViewModelTextValues(data: WrappedData): Record<string, string> {
  const badge = getFoundingBadge(data.joinDate);
  const seasonRanks = getSeasonRanks(data.seasonDetails);

  // Create a helper to add both naming conventions
  const addField = (num: number, value: string, result: Record<string, string>) => {
    result[`Txt ${num}`] = value;
    result[num.toString()] = value;
  };

  const result: Record<string, string> = {};

  // Screen 2 - Join Date
  addField(1, formatDate(data.joinDate), result);
  addField(2, data.daysSinceJoin.toString(), result);
  
  // Field 3: Badge Title Only (e.g. "Day One Legend")
  // The user reports that "Initial Value" text is appearing or duplication is happening.
  // We will set Field 3 to just the title.
  addField(3, badge.title, result);
  
  // Field 4: Badge Subtitle (e.g. "You were there at launch")
  // We ensure this is populated so it appears on the second line if the Rive file is set up for it.
  // Using a newline character at the START of the string to force it down visually
  addField(4, `\n${badge.subtitle}`, result);

  // Screen 3 - Activity Stats
  addField(5, data.daysSinceJoin.toString(), result);
  addField(6, formatNumber(data.tokensJunked), result);
  addField(7, formatNumber(data.nftsJunked), result);
  addField(8, data.itemsPerDay.toString(), result);
  addField(9, getAdaptiveMessage(data), result);

  // Screen 4 - SOL Reclaimed
  addField(10, formatSolAmount(data.solReclaimed), result);

  // Screen 5 - Seasons
  addField(11, `${data.seasonsParticipated}/${data.totalSeasons}`, result);
  addField(12, seasonRanks[0]?.rank || '-', result);
  addField(13, seasonRanks[0]?.status || 'N/A', result);
  addField(14, seasonRanks[1]?.rank || '-', result);
  addField(15, seasonRanks[1]?.status || 'N/A', result);
  addField(16, seasonRanks[2]?.rank || '-', result);
  addField(17, seasonRanks[2]?.status || 'N/A', result);
  addField(18, seasonRanks[3]?.rank || '-', result);
  addField(19, seasonRanks[3]?.status || 'N/A', result);
  addField(20, seasonRanks[4]?.rank || '-', result);

  // Screen 5 continued
  addField(21, seasonRanks[4]?.status || 'N/A', result);

  // Screen 6 - Lucky Draw
  addField(22, formatPoints(data.pointsContributedToLottery), result);
  addField(23, formatPoints(data.pointsContributedToLottery), result); // Entries
  addField(24, (data.luckyDrawWins?.length || 0).toString(), result);
  addField(25, getRngFaithMessage(data), result);
  addField(26, getBiggestWin(data), result);

  // Screen 7 - Chests
  addField(27, formatNumber(data.chestsOpened), result);
  addField(28, formatSolAmount(data.biggestSingleReclaim), result);
  addField(29, data.longestStreak.toString(), result);

  // Screen 8 - Longest Streak
  addField(30, data.longestStreak.toString(), result);

  // Format streak dates - handle single day and missing dates
  let streakDateDisplay = 'N/A';
  if (data.streakDates) {
    streakDateDisplay = formatDateRange(data.streakDates.start, data.streakDates.end);
  } else if (data.longestStreak === 1 && data.joinDate) {
    // If streak is 1 but no specific dates, show join date as fallback
    streakDateDisplay = formatSingleDate(data.joinDate);
  }
  addField(31, streakDateDisplay, result);

  // Screen 9 - Rank
  // Field 32: User rank (e.g., #123)
  // If no leaderboard rank, estimate based on lifetime points relative to total users
  let rankDisplay: string;
  let estimatedPercentile = data.percentile;
  if (data.overallRank) {
    rankDisplay = `#${formatNumber(data.overallRank)}`;
  } else if (data.totalUsers > 0) {
    // Estimate rank based on lifetime points - higher points = better estimated rank
    const lifetimePoints = Number(data.lifetimePoints) || 0;
    let estimatedRank: number;

    if (lifetimePoints >= 10000) {
      // High activity - estimate top 25%
      estimatedRank = Math.round(data.totalUsers * 0.25);
      estimatedPercentile = 75;
    } else if (lifetimePoints >= 5000) {
      // Good activity - estimate top 40%
      estimatedRank = Math.round(data.totalUsers * 0.4);
      estimatedPercentile = 60;
    } else if (lifetimePoints >= 1000) {
      // Moderate activity - estimate top 60%
      estimatedRank = Math.round(data.totalUsers * 0.6);
      estimatedPercentile = 40;
    } else if (lifetimePoints >= 100) {
      // Low activity - estimate top 80%
      estimatedRank = Math.round(data.totalUsers * 0.8);
      estimatedPercentile = 20;
    } else {
      // Minimal activity - near the bottom
      estimatedRank = data.totalUsers;
      estimatedPercentile = 5;
    }

    rankDisplay = `~#${formatNumber(estimatedRank)}`;
  } else {
    rankDisplay = '-';
  }
  addField(32, rankDisplay, result);
  // Field 33: Top percentage (e.g., Top 5%) - swapped with 34
  addField(33, `${Math.round(100 - estimatedPercentile)}%`, result);
  // Field 34: Percentile (e.g., 95%) - swapped with 33
  addField(34, `${Math.round(estimatedPercentile)}%`, result);
  // Field 35: Total users count
  addField(35, formatNumber(data.totalUsers), result);
  // Field 36: Additional rank info if needed
  addField(36, `${Math.round(estimatedPercentile)}%`, result);

   // Screen 10 - Personality
   // Field 37 = Personality Title (e.g. "Strategic Starter")
   const personalityTitle = getPersonalityTitle(data.personalityType);
   addField(37, personalityTitle, result);
 
   // Field 38 = "Your Pattern" label + Traits (Bullet points) ONLY
   // Per user feedback: Remove description ("The thrill of the unknown...")
   // Use - instead of bullet point for font compatibility
   const traits = data.personalityTraits?.slice(0, 3).map(t => `- ${t}`).join('\n') || '';
   
   // Format: "Your Pattern:\n\n[Traits]"
   const fullPatternText = `Your Pattern:\n\n${traits}`;
   addField(38, fullPatternText, result);
 
   // Field 39 = App Age at Join
   // Calculate platform age relative to Oct 28, 2025 (official launch date)
   const launchDateTs = new Date('2025-10-28T00:00:00Z').getTime();
   const joinDateTs = new Date(data.joinDate).getTime();
   const ageInMs = joinDateTs - launchDateTs;
   const platformAge = Math.max(0, Math.floor(ageInMs / (1000 * 60 * 60 * 24)));
   addField(39, ` ${platformAge} `, result);
 
   // Also try specific trait lines if the file uses separate inputs
   if (data.personalityTraits && data.personalityTraits.length > 0) {
     result['Txt 38_1'] = `- ${data.personalityTraits[0]}`;
     result['Txt 38_2'] = `- ${data.personalityTraits[1] || ''}`;
     result['Txt 38_3'] = `- ${data.personalityTraits[2] || ''}`;
   }
 
   // Screen 11 - Final / Days (shifted to 40)
   addField(40, ` ${platformAge} `, result);

  // Screen 13 - Gratitude / You're a # (stays at 41)
  // "You're not just a user. You're a [Badge Title]"
  addField(41, badge.title.toUpperCase(), result); // e.g. "FOUNDER" or "DAY ONE LEGEND"

  // Field 42 - Adaptive Streak Message (Your Dedication screen)
  // This is the adaptive message for the streak/dedication screen
  // Thresholds: 1 day, 2-3 days, 4-6 days, 7-13 days, 14+ days
  addField(42, getStreakMessage(data.longestStreak), result);

  // Override any "swipe up" instruction text to show horizontal swipe hint instead
  // Try various potential text run names that might control the instruction
  // Using empty string to hide the outdated "swipe up" text
  const hideSwipe = '';

  // Common naming patterns for intro/instruction text in Rive files
  // Screen 1 / Intro specific
  result['SwipeHint'] = hideSwipe;
  result['swipe_hint'] = hideSwipe;
  result['swipeHint'] = hideSwipe;
  result['Swipe Hint'] = hideSwipe;
  result['swipe hint'] = hideSwipe;
  result['SwipeUp'] = hideSwipe;
  result['swipe_up'] = hideSwipe;
  result['swipeUp'] = hideSwipe;
  result['Swipe Up'] = hideSwipe;
  result['swipe up'] = hideSwipe;

  // Generic instruction text
  result['Instruction'] = hideSwipe;
  result['instruction'] = hideSwipe;
  result['Instructions'] = hideSwipe;
  result['Hint'] = hideSwipe;
  result['hint'] = hideSwipe;
  result['CTA'] = hideSwipe;
  result['cta'] = hideSwipe;
  result['Cta'] = hideSwipe;

  // txt_ prefix variants
  result['txt_Hint'] = hideSwipe;
  result['txt_Instruction'] = hideSwipe;
  result['txt_SwipeHint'] = hideSwipe;
  result['txt_SwipeUp'] = hideSwipe;
  result['txt_Swipe'] = hideSwipe;
  result['txt_CTA'] = hideSwipe;

  // Txt prefix variants (with space)
  result['Txt Hint'] = hideSwipe;
  result['Txt Instruction'] = hideSwipe;
  result['Txt SwipeHint'] = hideSwipe;
  result['Txt Swipe'] = hideSwipe;
  result['Txt CTA'] = hideSwipe;

  // Screen 1 specific names
  result['Screen1_Hint'] = hideSwipe;
  result['Intro_Hint'] = hideSwipe;
  result['IntroHint'] = hideSwipe;
  result['introHint'] = hideSwipe;
  result['intro_hint'] = hideSwipe;
  result['Welcome_Hint'] = hideSwipe;
  result['WelcomeHint'] = hideSwipe;

  // Try numbered fields for intro screen (screen 1 might use early numbers)
  result['Txt 0'] = hideSwipe;
  result['0'] = hideSwipe;

  return result;
}

/**
 * Build all text run values for the Rive animation (LEGACY - path-based approach)
 *
 * Format: { "path": { "textRunName": "value" } }
 * Path is the nested artboard name (e.g., "Screen 2")
 * Text run name is the name given to the text run in Rive (e.g., "txt_Date")
 *
 * IMPORTANT: Text runs must be EXPORTED from the nested artboard to be accessible at runtime.
 * Just naming them isn't enough - they need to be exposed/exported in Rive.
 */
export function buildTextRunValues(data: WrappedData): Record<string, Record<string, string>> {
  const badge = getFoundingBadge(data.joinDate);
  const seasonRanks = getSeasonRanks(data.seasonDetails);

  // Calculate rank display with estimation for users not on leaderboard
  let legacyRankDisplay: string;
  let legacyPercentile = data.percentile;
  if (data.overallRank) {
    legacyRankDisplay = `#${formatNumber(data.overallRank)}`;
  } else if (data.totalUsers > 0) {
    // Estimate based on lifetime points
    const lifetimePoints = Number(data.lifetimePoints) || 0;
    let estimatedRank: number;

    if (lifetimePoints >= 10000) {
      estimatedRank = Math.round(data.totalUsers * 0.25);
      legacyPercentile = 75;
    } else if (lifetimePoints >= 5000) {
      estimatedRank = Math.round(data.totalUsers * 0.4);
      legacyPercentile = 60;
    } else if (lifetimePoints >= 1000) {
      estimatedRank = Math.round(data.totalUsers * 0.6);
      legacyPercentile = 40;
    } else if (lifetimePoints >= 100) {
      estimatedRank = Math.round(data.totalUsers * 0.8);
      legacyPercentile = 20;
    } else {
      estimatedRank = data.totalUsers;
      legacyPercentile = 5;
    }

    legacyRankDisplay = `~#${formatNumber(estimatedRank)}`;
  } else {
    legacyRankDisplay = '-';
  }

  return {
    // Screen 2 - Join Date
    'Screen 2': {
      txt_Date: formatDate(data.joinDate),
      txt_DayAgo: `${data.daysSinceJoin} days ago`,
      txt_BadgeTitle: badge.title,
      txt_BadgeSubTitle: badge.subtitle,
    },

    // Screen 3 - Activity Stats
    'Screen 3': {
      txt_FirstDays: data.daysSinceJoin.toString(),
      txt_Tokens: formatNumber(data.tokensJunked),
      txt_NFTs: formatNumber(data.nftsJunked),
      txt_AdaptiveMessaging: getAdaptiveMessage(data),
    },

    // Screen 4 - SOL Reclaimed
    'Screen 4': {
      txt_Sol: formatSolAmount(data.solReclaimed),
    },

    // Screen 5 - Seasons
    'Screen 5': {
      txt_SeasonsParticipated: `${data.seasonsParticipated} of ${data.totalSeasons}`,
      txt_S1Rank: seasonRanks[0]?.rank || '-',
      txt_S2Rank: seasonRanks[1]?.rank || '-',
      txt_S3Rank: seasonRanks[2]?.rank || '-',
      txt_S4Rank: seasonRanks[3]?.rank || '-',
      txt_S5Rank: seasonRanks[4]?.rank || '-',
      txt_S1Status: seasonRanks[0]?.status || 'N/A',
      txt_S2Status: seasonRanks[1]?.status || 'N/A',
      txt_S3Status: seasonRanks[2]?.status || 'N/A',
      txt_S4Status: seasonRanks[3]?.status || 'N/A',
      txt_S5Status: seasonRanks[4]?.status || 'N/A',
    },

    // Screen 6 - Points & Lottery
    'Screen 6': {
      txt_Points: formatPoints(data.lifetimePoints),
      txt_Entires: formatPoints(data.pointsContributedToLottery),
      txt_Draws: data.luckyDrawWins?.length.toString() || '0',
      txt_BiggestWin: getBiggestWin(data),
      txt_Prize: getTotalPrize(data),
    },

    // Screen 7 - Chests
    'Screen 7': {
      txt_ChestOpened: formatNumber(data.chestsOpened),
      txt_BestFind: formatSolAmount(data.biggestSingleReclaim),
      txt_Streak: data.longestStreak.toString(),
    },

    // Screen 8 - Longest Streak
    'Screen 8': {
      txt_LongestStreak: data.longestStreak.toString(),
      txt_LongestStreakDate: data.streakDates
        ? formatDateRange(data.streakDates.start, data.streakDates.end)
        : 'N/A',
      txt_LongestStreakAdotMsg: getStreakMessage(data.longestStreak),
    },

    // Screen 9 - Rank
    'Screen 9': {
      txt_Rank: legacyRankDisplay,
      txt_G_Users: formatNumber(data.totalUsers),
      txt_TopUser: `Top ${Math.round(100 - legacyPercentile)}%`,
      txt_BetterThan: `${Math.round(legacyPercentile)}% of users`,
    },

    // Screen 10 - Personality
    'Screen 10': {
      txt_JS_Title: getPersonalityTitle(data.personalityType),
      txt_JS_SubTitles: data.personalityTraits?.slice(0, 3).map(t => `- ${t}`).join('\n') || '',
      
      // Try singular/numbered variants in case the file expects them
      txt_JS_SubTitle: data.personalityTraits?.slice(0, 3).map(t => `- ${t}`).join('\n') || '',
      txt_JS_SubTitles_1: `- ${data.personalityTraits?.[0] || ''}`,
      txt_JS_SubTitles_2: `- ${data.personalityTraits?.[1] || ''}`,
      txt_JS_SubTitles_3: `- ${data.personalityTraits?.[2] || ''}`,
      txt_JS_SubTitle_1: `- ${data.personalityTraits?.[0] || ''}`,
      txt_JS_SubTitle_2: `- ${data.personalityTraits?.[1] || ''}`,
      txt_JS_SubTitle_3: `- ${data.personalityTraits?.[2] || ''}`,
      
      // Try generic traits mapping
      txt_Traits: data.personalityTraits?.slice(0, 3).map(t => `- ${t}`).join('\n') || '',
    },

    // Screen 11 - Days Old
    'Screen 11': {
      txt_DaysOld: data.daysSinceJoin.toString(),
    },
  };
}
